import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { NetworkSwitchModal } from '@/components/ui/NetworkSwitchModal';
import { ToastProvider, ToastViewport } from '@/components/ui/toast';
import { Dashboard } from '@/pages/Dashboard';
import { Invoices } from '@/pages/Invoices';
import { Payments } from '@/pages/Payments';
import { Vault } from '@/pages/Vault';
import { Market } from '@/pages/Market';
import { Settings } from '@/pages/Settings';
import { Analytics } from '@/pages/Analytics';
import { NetworkStats } from '@/pages/NetworkStats';
import { useWalletUtils } from '@/hooks/useWalletUtils';
import { useNetworkNotifications } from '@/hooks/useNetworkNotifications';
import { useInvoiceContract } from '@/hooks/useInvoiceContract';
import { useContractVersionCheck } from '@/hooks/useContractVersionCheck';
import { invoiceStorage } from '@/services/invoice-storage';
import { firebaseVisitTracker as visitTracker } from '@/services/visit-tracker-firebase';
import { isSupabaseConfigured, testSupabaseConnection } from '@/lib/supabase';
import { initializeSupabase } from '@/lib/supabase-init';
import { isFirebaseConfigured, testFirebaseConnection } from '@/lib/firebase';
import { verifyAllContracts } from '@/utils/contract-verifier';
import { testExplorerConnection } from '@/services/explorer-api';
import { Invoice } from '@/types/invoice';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [hasShownNetworkPrompt, setHasShownNetworkPrompt] = useState(false);
  const [localInvoices, setLocalInvoices] = useState<Invoice[]>([]);

  const { isConnected, chainId } = useAccount();
  const { isMezoTestnet } = useWalletUtils();
  
  // Get invoices from blockchain
  const { invoices, stats } = useInvoiceContract();
  
  // Load local invoices and refresh periodically (only when wallet is connected)
  const { address } = useAccount();
  useEffect(() => {
    if (!address) {
      setLocalInvoices([]);
      return;
    }
    
    const loadLocalInvoices = () => {
      const drafts = invoiceStorage.listDrafts(address);
      setLocalInvoices(drafts);
    };
    
    loadLocalInvoices();
    
    // Refresh every 5 seconds to keep data in sync
    const interval = setInterval(loadLocalInvoices, 5000);
    
    return () => clearInterval(interval);
  }, [address]);

  // Combine blockchain invoices with local invoices safely (avoid duplicates)
  const allInvoices = (() => {
    // Build a set of blockchain client codes for de-duplication
    const chainClientCodes = new Set<string>(
      invoices.map(i => i.clientCode).filter(Boolean) as string[]
    );

    // Only include drafts that are not already on-chain (match by clientCode)
    // and are still pending sync
    const filteredDrafts = localInvoices.filter(d => {
      const notOnChain = d.clientCode ? !chainClientCodes.has(d.clientCode) : true;
      const isPending = (d as any).syncPending !== false; // default to true if undefined
      return notOnChain && isPending;
    });

    // Merge lists and sort by createdAt (newest first)
    const merged = [...invoices, ...filteredDrafts];
    return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  })();
  
  // Enable network notifications
  useNetworkNotifications();

  // Check if contracts have been redeployed and reset MATS if needed
  useContractVersionCheck();

  // Initialize and verify data sources on app startup: RPC → Explorer → Firebase
  useEffect(() => {
    const setupDataSources = async () => {
      // 1. RPC + contracts (primary for live chain reads)
      console.log('[App] 🔍 Verifying RPC and smart contracts...');
      await verifyAllContracts().catch(err => {
        console.error('[App] Contract verification error:', err);
      });

      // 2. Explorer API (Blockscout – all-time invoice data, used alongside RPC)
      console.log('[App] 🔍 Verifying Explorer API...');
      const explorerOk = await testExplorerConnection().catch(() => false);
      if (explorerOk) {
        console.log('[App] ✅ Explorer API reachable – Explorer + RPC both used for stats');
      } else {
        console.warn('[App] ⚠ Explorer API unreachable – stats will use RPC only');
      }

      // 3. Firebase (primary for visit tracking)
      if (isFirebaseConfigured()) {
        console.log('[App] 🔥 Firebase configured (visit tracking)');
        const isFirebaseConnected = await testFirebaseConnection();
        if (isFirebaseConnected) {
          console.log('[App] ✅ Firebase connected – global visit tracking active');
        } else {
          console.warn('[App] ⚠ Firebase connection test failed – check Realtime DB rules');
        }
      } else {
        console.warn('[App] ⚠ Firebase not configured – visit tracking uses localStorage (per-browser)');
      }

      // Supabase (optional fallback; not used for visits when Firebase is primary)
      if (isSupabaseConfigured()) {
        console.log('[App] 📊 Supabase configured (optional)');
        await initializeSupabase().catch(() => {});
        const supabaseOk = await testSupabaseConnection();
        if (supabaseOk) console.log('[App] ✓ Supabase verified');
        else console.warn('[App] ⚠ Supabase connection test failed');
      }
    };

    setupDataSources().catch(err => {
      console.error('[App] Failed to setup data sources:', err);
    });
  }, []);

  // Track website visit when app loads
  useEffect(() => {
    visitTracker.trackVisit().catch(err => {
      console.warn('Failed to track visit:', err);
    });
  }, []);

  // Show network modal when user connects to a non-Mezo network
  useEffect(() => {
    if (isConnected && !isMezoTestnet(chainId) && !hasShownNetworkPrompt) {
      setShowNetworkModal(true);
      setHasShownNetworkPrompt(true);
    }
  }, [isConnected, chainId, isMezoTestnet, hasShownNetworkPrompt]);

  // Reset the prompt flag when user disconnects
  useEffect(() => {
    if (!isConnected) {
      setHasShownNetworkPrompt(false);
    }
  }, [isConnected]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveTab} invoices={allInvoices} stats={stats} />;
      case 'invoices':
        return <Invoices invoices={allInvoices} />;
      case 'payments':
        return <Payments invoices={allInvoices} />;
      case 'vault':
        return <Vault />;
      case 'market':
        return <Market />;
      case 'network-stats':
        return <NetworkStats />;
      case 'settings':
        return <Settings />;
      case 'analytics':
        return <Analytics invoices={allInvoices} />;
      default:
        return <Dashboard onNavigate={setActiveTab} invoices={allInvoices} stats={stats} />;
    }
  };

  return (
    <ToastProvider>
      <div className="flex h-screen bg-background text-foreground overflow-hidden safe-area-inset">
        {/* Sidebar */}
        <Sidebar 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          onShowNetworkModal={() => setShowNetworkModal(true)}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
          {/* Page Content with Header inside scrollable area */}
          <div className="flex-1 overflow-auto relative">
            {/* Header - sticky within scroll container */}
            <Header 
              onShowNetworkModal={() => setShowNetworkModal(true)}
            />
            
            {/* Page Content */}
            <div className="pt-0 lg:pt-0">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Network Switch Modal */}
      <NetworkSwitchModal 
        isOpen={showNetworkModal}
        onClose={() => setShowNetworkModal(false)}
      />
      
      {/* Toast Viewport */}
      <ToastViewport />
    </ToastProvider>
  );
}

export default App;